import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Sparkles } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Section from "@/components/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/useLanguage";

type Advice = {
  summary: string;
  nextSteps: { title: string; detail: string; timeframe: string }[];
  services: { id: "thought-leadership" | "transformation" | "executive-advisory"; why: string }[];
};

const COPY = {
  en: {
    title: "AI readiness advisor",
    intro: "Describe your organisation's AI skills and governance needs. You'll get tailored next steps and the AI-labbet services that fit best. Nothing you write is stored.",
    role: "Your role", orgSize: "Organisation size",
    skills: "Current AI skills and usage *", skillsPh: "e.g. A few teams use ChatGPT ad hoc, management has limited AI knowledge…",
    gov: "Governance needs and concerns", govPh: "e.g. No AI policy yet, unsure about the EU AI Act, data security…",
    goals: "Goals for the next 12 months",
    submit: "Get my next steps", loading: "Analysing…",
    summary: "Your situation", steps: "Recommended next steps", services: "Relevant AI-labbet services",
    cta: "Book a call to discuss", error: "Something went wrong. Please try again in a moment.",
    note: "AI-generated suggestions — a starting point for a conversation, not formal advice.",
    names: { "thought-leadership": "Thought Leadership", transformation: "Transformation & Enablement", "executive-advisory": "Executive Advisory" },
  },
  sv: {
    title: "AI-rådgivaren",
    intro: "Beskriv er organisations AI-kompetens och behov av styrning. Du får skräddarsydda nästa steg och de tjänster från AI-labbet som passar bäst. Inget du skriver sparas.",
    role: "Din roll", orgSize: "Organisationens storlek",
    skills: "Nuvarande AI-kompetens och användning *", skillsPh: "t.ex. Några team använder ChatGPT sporadiskt, ledningen har begränsad AI-kunskap…",
    gov: "Behov och frågor kring styrning", govPh: "t.ex. Ingen AI-policy ännu, osäkra kring EU:s AI-förordning, datasäkerhet…",
    goals: "Mål för kommande 12 månader",
    submit: "Få mina nästa steg", loading: "Analyserar…",
    summary: "Er situation", steps: "Rekommenderade nästa steg", services: "Relevanta tjänster från AI-labbet",
    cta: "Boka ett samtal", error: "Något gick fel. Försök igen om en stund.",
    note: "AI-genererade förslag — en utgångspunkt för samtal, inte formell rådgivning.",
    names: { "thought-leadership": "Thought Leadership", transformation: "Transformation & Enablement", "executive-advisory": "Executive Advisory" },
  },
};

const AIAdvisor = () => {
  const { lang: current, localize } = useLanguage();
  const lang = current === "sv" ? "sv" : "en";
  const c = COPY[lang];
  const [form, setForm] = useState({ role: "", orgSize: "", skills: "", governance: "", goals: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [advice, setAdvice] = useState<Advice | null>(null);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError(""); setAdvice(null);
    const { data, error } = await supabase.functions.invoke("ai-advisor", { body: { ...form, language: lang } });
    setLoading(false);
    if (error || !data?.nextSteps) setError(c.error);
    else setAdvice(data as Advice);
  };

  return (
    <>
      <SeoHead
        title={{ en: "AI readiness advisor | AI-labbet", sv: "AI-rådgivaren | AI-labbet" }}
        description={{
          en: "Describe your organisation's AI skills and governance needs and get tailored next steps from AI-labbet.",
          sv: "Beskriv er AI-kompetens och styrningsbehov och få skräddarsydda nästa steg från AI-labbet.",
        }}
        path="/ai-advisor"
      />
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow">
          <Section title={c.title} titleAs="h1" containerSize="narrow">
            <div className="max-w-2xl mx-auto space-y-8">
              <p className="text-muted-foreground">{c.intro}</p>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label htmlFor="role">{c.role}</Label><Input id="role" maxLength={120} value={form.role} onChange={set("role")} /></div>
                  <div><Label htmlFor="org">{c.orgSize}</Label><Input id="org" maxLength={60} value={form.orgSize} onChange={set("orgSize")} /></div>
                </div>
                <div><Label htmlFor="skills">{c.skills}</Label><Textarea id="skills" required minLength={10} maxLength={2000} rows={4} placeholder={c.skillsPh} value={form.skills} onChange={set("skills")} /></div>
                <div><Label htmlFor="gov">{c.gov}</Label><Textarea id="gov" maxLength={2000} rows={3} placeholder={c.govPh} value={form.governance} onChange={set("governance")} /></div>
                <div><Label htmlFor="goals">{c.goals}</Label><Textarea id="goals" maxLength={1000} rows={2} value={form.goals} onChange={set("goals")} /></div>
                <Button type="submit" disabled={loading} size="lg">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                  {loading ? c.loading : c.submit}
                </Button>
                {error && <p className="text-destructive text-sm">{error}</p>}
              </form>

              {advice && (
                <div className="space-y-6 border-t border-border pt-8">
                  <div><h2 className="text-2xl font-semibold mb-2">{c.summary}</h2><p>{advice.summary}</p></div>
                  <div>
                    <h2 className="text-2xl font-semibold mb-3">{c.steps}</h2>
                    <ol className="space-y-3">
                      {advice.nextSteps.map((s, i) => (
                        <li key={i} className="rounded-lg border border-border bg-card p-4">
                          <div className="flex justify-between gap-4"><span className="font-medium">{i + 1}. {s.title}</span><span className="text-sm text-muted-foreground whitespace-nowrap">{s.timeframe}</span></div>
                          <p className="text-sm text-muted-foreground mt-1">{s.detail}</p>
                        </li>
                      ))}
                    </ol>
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold mb-3">{c.services}</h2>
                    <ul className="space-y-3">
                      {advice.services.map((s) => (
                        <li key={s.id} className="rounded-lg border border-primary/30 bg-primary/5 p-4">
                          <Link to={localize("/services")} className="font-medium text-primary hover:underline">{c.names[s.id] ?? s.id}</Link>
                          <p className="text-sm mt-1">{s.why}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button asChild size="lg"><Link to={localize("/contact")}>{c.cta}</Link></Button>
                  <p className="text-xs text-muted-foreground">{c.note}</p>
                </div>
              )}
            </div>
          </Section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default AIAdvisor;
